import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { PhoneInputShadcn } from "@/components/ui/phone-input-shadcn";
import { Customer, BusinessAccount } from "@/api.types";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DockedContainer } from "@/components/docked-container";
import { logger } from "@/services/logger";

const profileSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  preferredName: z.string().optional(),
  email: z.string().email("Invalid email address"),
  phone: z
    .string()
    .refine(isPossiblePhoneNumber, { message: "Invalid phone number" }),
});

const businessAccountSchema = z.object({
  companyName: z.string().min(1, "Company name is required"),
  taxNumber: z.string().optional(),
});

export default function OnboardingStep1Screen() {
  const navigate = useNavigate();
  const { token, customer, user } = useSession();
  const queryClient = useQueryClient();
  const [isBusinessAccount, setIsBusinessAccount] = useState(false);

  const form = useForm<z.infer<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      preferredName: "",
      email: user?.email ?? "",
      phone: user?.phone ? `${user?.phone}` : "",
    },
  });

  const businessForm = useForm<z.infer<typeof businessAccountSchema>>({
    resolver: zodResolver(businessAccountSchema),
    defaultValues: {
      companyName: "",
      taxNumber: "",
    },
  });

  const initializeCustomerMutation = useMutation({
    mutationFn: async (data: {
      customerId: string;
      profile: {
        firstName: string;
        lastName: string;
        preferredName?: string;
        email: string;
        phone: string;
      };
      businessAccount?: {
        isBusinessAccount: boolean;
        companyName: string;
        taxNumber?: string;
      };
    }) => {
      const response = await axios.post<{
        customer: Customer;
        businessAccount: BusinessAccount;
        customerId?: string;
      }>(`${import.meta.env.VITE_API_URL}/api/v1/customers/initialize`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return response.data;
    },
    onSuccess: async () => {
      // Invalidate relevant queries
      await queryClient.invalidateQueries({
        queryKey: ["customer", customer?.id],
      });
      // Navigate to next step
      await navigate("/onboarding/step-2");
    },
    onError: (error) => {
      logger.error("Error initializing customer:", error);
      // You can add toast notification here
    },
  });

  const onSubmit = async () => {
    // Trigger validation for all forms
    const profileValid = await form.trigger();
    const businessValid = isBusinessAccount && (await businessForm.trigger());

    // Check if all required forms are valid
    const isValid = profileValid && (!isBusinessAccount || businessValid);

    if (!isValid) {
      // If any form is invalid, return early
      return;
    }

    const profileValues = form.getValues();
    const businessValues = businessForm.getValues();

    initializeCustomerMutation.mutate({
      customerId: customer?.id ?? "",
      profile: {
        firstName: profileValues.firstName,
        lastName: profileValues.lastName,
        preferredName: profileValues.preferredName,
        email: profileValues.email,
        phone: profileValues.phone,
      },
      businessAccount: isBusinessAccount
        ? {
            isBusinessAccount: true,
            companyName: businessValues.companyName,
            taxNumber: businessValues.taxNumber,
          }
        : undefined,
    });
  };

  return (
    <div>
      <div className="flex flex-col flex-1 items-center p-4 sm:p-6 pb-32">
        <div className="w-full max-w-4xl space-y-8 pb-24">
          {/* Profile Form */}
          <Form {...form}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="preferredName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preferred Name (Optional)</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <PhoneInputShadcn
                          placeholder="Enter phone number"
                          {...field}
                          value={field.value}
                          onChange={(value) => field.onChange(value ?? "")}
                          defaultCountry="CA"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </Form>

          <Separator />

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="business-account"
                checked={isBusinessAccount}
                onCheckedChange={(checked) => {
                  setIsBusinessAccount(checked === true);
                }}
              />
              <label
                htmlFor="business-account"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                This is a business account
              </label>
            </div>

            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This can not be changed after the account is created.
              </AlertDescription>
            </Alert>

            <Form {...businessForm}>
              <div
                className={`space-y-4 ${!isBusinessAccount ? "opacity-50" : ""}`}
              >
                <FormField
                  control={businessForm.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={!isBusinessAccount} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={businessForm.control}
                  name="taxNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tax Number (BN)</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={!isBusinessAccount} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Form>
          </div>
        </div>
      </div>
      <DockedContainer>
        <div className="lg:max-w-7xl mx-auto px-4 lg:px-8">
          <Button
            onClick={onSubmit}
            disabled={initializeCustomerMutation.isPending}
            className="w-[300px] text-lg py-6 bg-white text-black hover:bg-white/90"
          >
            {initializeCustomerMutation.isPending ? "Saving..." : "Continue"}
          </Button>
        </div>
      </DockedContainer>
    </div>
  );
}
